/** Thin fetch wrapper around the Bandwagn Worker. The JWT lives in localStorage. */
import type {
  Activity, Band, BandSearchHit, ChatMessage, ChatRoom, GeoResult, MatchResult, Photo, Profile, PublicProfile,
  Post, PostLimits, SavedSearch, SearchCriteria, Settings, Wants,
} from './types';
import type { Attachment } from './media';

export const API_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';

const TOKEN_KEY = 'bandwagn.token';
let token: string | null = safeGet(TOKEN_KEY);
const listeners = new Set<() => void>();

function safeGet(k: string): string | null { try { return localStorage.getItem(k); } catch { return null; } }
function safeSet(k: string, v: string | null) { try { v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* ignore */ } }

export const getToken = (): string | null => token;
export function setToken(t: string | null) { token = t; safeSet(TOKEN_KEY, t); listeners.forEach((l) => l()); }
export function onAuthChange(l: () => void): () => void { listeners.add(l); return () => listeners.delete(l); }
export const logout = () => setToken(null);

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && typeof init.body === 'string') headers.set('Content-Type', 'application/json');
  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  // A dead token (expired, or the user row is gone after a QA DB rebuild) sends the app back to login.
  if (token && (res.status === 401 || (res.status === 404 && path === '/me'))) setToken(null);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (body as any).error ?? `HTTP ${res.status}`);
  return body as T;
}

export const photoSrc = (url: string | null): string | null => (url ? `${API_URL}${url}` : null);
export const chatSocketUrl = (roomId: string): string => `${API_URL.replace(/^http/, 'ws')}/chat/ws/${encodeURIComponent(roomId)}`;
/** Browser navigation target for a social sign-in or link. On native the return is the app's URL scheme. */
export const oauthStartUrl = (provider: string, mode: 'login' | 'link', nativeScheme?: string): string => {
  const base = nativeScheme ?? window.location.origin;
  const ret = `${base}/${mode === 'login' ? 'login' : 'settings'}`;
  const t = mode === 'link' && token ? `&token=${encodeURIComponent(token)}` : '';
  return `${API_URL}/auth/${provider}/start?mode=${mode}&return=${encodeURIComponent(ret)}${t}`;
};

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });
const upload = (field: string, file: File): RequestInit => { const fd = new FormData(); fd.append(field, file); return { method: 'POST', body: fd }; };

type AuthResult = { token: string; userId: string; created: boolean };

export const api = {
  // auth
  providers: () => request<{ providers: string[] }>('/auth/providers'),
  register: (email: string, password: string, displayName: string) => request<AuthResult>('/auth/register', { method: 'POST', ...json({ email, password, displayName }) }),
  loginEmail: (email: string, password: string) => request<AuthResult>('/auth/login', { method: 'POST', ...json({ email, password }) }),
  loginDevice: (deviceId: string) => request<AuthResult>('/auth/login', { method: 'POST', ...json({ deviceId }) }),

  // profile
  me: () => request<Profile>('/me'),
  updateMe: (patch: Partial<Pick<Profile, 'displayName' | 'birthdate' | 'ageHidden' | 'looking' | 'showsPlayed' | 'bio' | 'city' | 'lat' | 'lng'>>) =>
    request<Profile>('/me', { method: 'PATCH', ...json(patch) }),
  setInstruments: (instruments: string[]) => request<{ instruments: string[] }>('/me/instruments', { method: 'PUT', ...json({ instruments }) }),
  setGenres: (genres: string[]) => request<{ genres: string[] }>('/me/genres', { method: 'PUT', ...json({ genres }) }),
  setWants: (wants: Wants) => request<{ wants: Wants }>('/me/wants', { method: 'PUT', ...json(wants) }),
  setBands: (bands: { mbid: string; top: boolean }[]) => request<Profile>('/me/bands', { method: 'PUT', ...json({ bands }) }),
  setBandHistory: (entries: Profile['bandHistory']) => request<Profile>('/me/band-history', { method: 'PUT', ...json({ entries }) }),
  setLinks: (links: Profile['links']) => request<Profile>('/me/links', { method: 'PUT', ...json({ links }) }),
  uploadPhoto: (file: File) => request<{ photos: Photo[] }>('/me/photos', upload('photo', file)),
  replacePhoto: (id: string, file: File) => request<{ photos: Photo[] }>(`/me/photos/${id}`, { ...upload('photo', file), method: 'PUT' }),
  deletePhoto: (id: string) => request<{ photos: Photo[] }>(`/me/photos/${id}`, { method: 'DELETE' }),
  reorderPhotos: (ids: string[]) => request<{ photos: Photo[] }>('/me/photos/order', { method: 'PUT', ...json({ ids }) }),
  uploadBackground: (file: File) => request<{ backgroundUrl: string }>('/me/background', upload('background', file)),
  deleteBackground: () => request<{ backgroundUrl: null }>('/me/background', { method: 'DELETE' }),

  // catalog
  instruments: () => request<{ instruments: string[] }>('/meta/instruments'),
  suggestInstruments: (q: string) => request<{ results: string[] }>(`/bands/instruments?q=${encodeURIComponent(q)}`),
  suggestGenres: (q: string) => request<{ results: string[] }>(`/bands/genres?q=${encodeURIComponent(q)}`),
  searchBands: (q: string) => request<{ results: BandSearchHit[] }>(`/bands/search?q=${encodeURIComponent(q)}`),
  band: (mbid: string) => request<Band>(`/bands/${mbid}`),
  geocode: (q: string) => request<{ results: GeoResult[] }>(`/geo/search?q=${encodeURIComponent(q)}`),
  reverseGeocode: (lat: number, lng: number) => request<GeoResult>(`/geo/reverse?lat=${lat}&lng=${lng}`),

  // other users
  user: (id: string) => request<PublicProfile>(`/users/${id}`),
  like: (id: string) => request<{ likes: number; likedByMe: boolean }>(`/users/${id}/like`, { method: 'POST' }),
  unlike: (id: string) => request<{ likes: number; likedByMe: boolean }>(`/users/${id}/like`, { method: 'DELETE' }),
  follow: (id: string) => request<{ ok: true }>(`/users/${id}/follow`, { method: 'POST' }),
  unfollow: (id: string) => request<{ ok: true }>(`/users/${id}/follow`, { method: 'DELETE' }),

  // posts feed
  postLimits: () => request<PostLimits>('/posts/limits'),
  feedPosts: (before = 0) => request<{ posts: Post[]; hasMore: boolean }>(`/posts/feed?before=${before}`),
  userPosts: (id: string, before = 0) => request<{ posts: Post[]; hasMore: boolean }>(`/posts/user/${id}?before=${before}`),
  createPost: (body: string, attachments: Attachment[]) => {
    const fd = new FormData();
    fd.append('body', body);
    attachments.forEach((a, i) => {
      fd.append('media', a.file);
      if (a.width) { fd.append(`width_${i}`, String(a.width)); fd.append(`height_${i}`, String(a.height)); }
      if (a.durationMs) fd.append(`duration_${i}`, String(a.durationMs));
    });
    return request<Post>('/posts', { method: 'POST', body: fd });
  },
  deletePost: (id: string) => request<{ ok: true }>(`/posts/${id}`, { method: 'DELETE' }),
  interest: (id: string) => request<{ roomId: string }>(`/users/${id}/interest`, { method: 'POST' }),

  // searches
  runSearch: (criteria: SearchCriteria) => request<{ results: MatchResult[] }>('/searches/run', { method: 'POST', ...json(criteria) }),
  listSearches: () => request<{ searches: SavedSearch[] }>('/searches'),
  getSearch: (id: string) => request<SavedSearch>(`/searches/${id}`),
  createSearch: (name: string, criteria: SearchCriteria) => request<SavedSearch>('/searches', { method: 'POST', ...json({ name, ...criteria }) }),
  updateSearch: (id: string, name: string, criteria: SearchCriteria) => request<SavedSearch>(`/searches/${id}`, { method: 'PUT', ...json({ name, ...criteria }) }),
  deleteSearch: (id: string) => request<{ ok: true }>(`/searches/${id}`, { method: 'DELETE' }),
  runSavedSearch: (id: string) => request<{ search: SavedSearch; results: MatchResult[] }>(`/searches/${id}/run`, { method: 'POST' }),

  // chat
  chatRooms: () => request<{ rooms: ChatRoom[] }>('/chat/rooms'),
  chatUnread: () => request<{ unread: number }>('/chat/unread'),
  openDm: (userId: string) => request<{ roomId: string }>(`/chat/dms/${userId}`, { method: 'POST' }),
  hideDm: (roomId: string) => request<{ ok: true }>(`/chat/dms/${roomId}`, { method: 'DELETE' }),
  chatMessages: (roomId: string, before = 0) => request<{ messages: ChatMessage[]; hasMore: boolean }>(`/chat/rooms/${roomId}/messages?before=${before}`),
  sendMessageHttp: (roomId: string, body: string) => request<ChatMessage>(`/chat/rooms/${roomId}/messages`, { method: 'POST', ...json({ body }) }),
  markSeen: (roomId: string) => request<{ ok: true }>(`/chat/rooms/${roomId}/seen`, { method: 'POST' }),
  block: (userId: string) => request<{ ok: true }>(`/chat/blocks/${userId}`, { method: 'POST' }),
  unblock: (userId: string) => request<{ ok: true }>(`/chat/blocks/${userId}`, { method: 'DELETE' }),

  // feed + settings
  feed: () => request<{ activities: Activity[]; unread: number }>('/feed'),
  feedUnread: () => request<{ unread: number }>('/feed/unread'),
  feedSeen: () => request<{ ok: true }>('/feed/seen', { method: 'POST' }),
  settings: () => request<Settings>('/settings'),
  updateSettings: (patch: { pushEnabled?: boolean; password?: string }) => request<{ ok: true }>('/settings', { method: 'PATCH', ...json(patch) }),
  registerPushToken: (token: string, platform: 'ios' | 'android' | 'web') => request<{ ok: true }>('/settings/push-token', { method: 'POST', ...json({ token, platform }) }),
  deleteAccount: () => request<{ ok: true }>('/me', { method: 'DELETE', ...json({ confirm: 'DELETE' }) }),
  unlinkIdentity: (provider: string) => request<{ ok: true }>(`/settings/identities/${provider}`, { method: 'DELETE' }),
};
